import { getUnsignedConsents, isConsentResolvedByUser } from "@/features/consent/utils/consentGating";
import {
    trackerDueForTodo,
    trackersViewForTodo,
    type DiaryForTodoCounters,
} from "@/features/diaries/hooks/diaryForTodoCounters";
import {
    getFormsToDoCounts,
    isFormRelevantForViewer,
    type FormsAuthCtx,
} from "@/features/diaries/features/new/hooks/useFormsToDo";
import { getTrackerDisplayLabel } from "@/features/diaries/utils/displayLabels";
import {
    patientNeedsHospitalNumberSetup,
    patientNeedsReferenceDateSetup,
    patientNeedsSideSetupRoute,
} from "@/features/diaries/utils/missingDetailsBuilders";
import { getPatientSetupStepSequence } from "@/features/diaries/utils/setupNavigation";
import type { ConsentViewT } from "@/types/database/consents";
import type { StatefulTrackerViewT } from "@patient-watch/types";
import type { StatefulDiaryT } from "@/types/database/diaries";
import type { FlowPhase } from "@patient-watch/ui";

export type JourneyPhaseKey = "onboarding" | "setup" | "consent" | "forms";

export interface TodoItem {
    key: string;
    label: string;
    done: boolean;
}

export type JourneyTodos = Record<JourneyPhaseKey, TodoItem[]>;

const DEFAULT_PHASE_LABELS: Record<JourneyPhaseKey, string> = {
    onboarding: "Profile",
    setup: "Setup",
    consent: "Consent",
    forms: "Forms",
};

export type JourneyPhaseLabels = Record<JourneyPhaseKey, string>;

export interface SetupTodoLabels {
    hospitalNumber: string;
    procedureDate: string;
    side: string;
}

const DEFAULT_SETUP_LABELS: SetupTodoLabels = {
    hospitalNumber: "Hospital number",
    procedureDate: "Procedure date",
    side: "Side",
};

/** Minimal profile shape the onboarding checks need (mirrors ProfileNotificationCount). */
export interface OnboardingProfile {
    title?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    date_of_birth?: string | null;
    email?: string | null;
    phone?: string | null;
    organisations?: unknown[] | null;
    user_meta_data?: unknown;
}

export interface OnboardingLabels {
    name: string;
    dateOfBirth: string;
    email: string;
    phone: string;
    organisation: string;
}

/** Onboarding "things to do" — same rules as the header ProfileNotificationCount.
 * Skip flags do not count as done: missing phone/email stay incomplete in the UI.
 */
export function buildOnboardingTodos(
    profile: OnboardingProfile | null | undefined,
    labels: OnboardingLabels
): TodoItem[] {
    const p = profile ?? {};
    return [
        { key: "name", label: labels.name, done: Boolean(p.title && p.first_name && p.last_name) },
        { key: "dateOfBirth", label: labels.dateOfBirth, done: Boolean(p.date_of_birth) },
        { key: "email", label: labels.email, done: Boolean(p.email?.trim()) },
        { key: "phone", label: labels.phone, done: Boolean(p.phone?.trim()) },
        { key: "organisation", label: labels.organisation, done: Boolean(p.organisations?.length) },
    ];
}

/** Outstanding diary setup steps only — empty once there is nothing left to set up.
 * "I don't know yet" on procedure date counts as done so the patient can move on.
 */
export function buildSetupTodos(diary: StatefulDiaryT, labels: SetupTodoLabels = DEFAULT_SETUP_LABELS): TodoItem[] {
    const items: TodoItem[] = [];
    if (patientNeedsHospitalNumberSetup(diary))
        items.push({ key: "hospital-number", label: labels.hospitalNumber, done: false });
    if (patientNeedsReferenceDateSetup(diary))
        items.push({ key: "reference-date", label: labels.procedureDate, done: false });
    if (patientNeedsSideSetupRoute(diary)) items.push({ key: "side", label: labels.side, done: false });
    return items;
}

/**
 * Whether the patient setup phase is part of this diary's flow at all.
 *
 * A shared attribute on its own is not enough. Quickstarts always create one, and a
 * procedure date supplied with the diary (hospital number off, no side) is not work
 * for the patient. Showing that as a completed Set-up bar is wrong.
 *
 * The phase stays visible after the patient finishes hospital number or side, because
 * those steps remain part of the setup sequence once the values are saved. A missing
 * procedure date still counts, so the bar appears while that step is outstanding.
 */
export function isSetupApplicableForDiary(diary: StatefulDiaryT | null | undefined): boolean {
    if (!diary) return false;
    return getPatientSetupStepSequence(diary).length > 0;
}

/** Diary template includes side collection (surgeries, injections, or side-specific forms). */
export function diaryHasSideSetupTemplate(diary: StatefulDiaryT): boolean {
    const hasSurgeries = (diary.events_view ?? []).some((event) => (event?.surgeries_view ?? []).length > 0);
    if (hasSurgeries) return true;

    const hasInjectionDetails = (diary.events_view ?? []).some((event) =>
        (event?.injections_view ?? []).some((injection) => (injection?.injection_details ?? []).length > 0)
    );
    if (hasInjectionDetails) return true;

    const hasShockwaveDetails = (diary.events_view ?? []).some((event) =>
        (event?.shockwaves_view ?? []).some((shockwave) => (shockwave?.shockwave_details ?? []).length > 0)
    );
    if (hasShockwaveDetails) return true;

    return (diary.trackers_view ?? []).some(
        (tracker) => (tracker as StatefulTrackerViewT).form?.is_side_specific === true
    );
}

/** Consent steps — one per consent on the diary, done when signed. Empty when the diary needs no consent. */
export function buildConsentTodos(consents: ConsentViewT[] | null | undefined, userId: string | undefined): TodoItem[] {
    if (!userId) return [];
    return (consents ?? []).map((c) => ({
        key: c.consent_id ?? "",
        label: c.consent_name ?? "Consent",
        // Declined consents are a deliberate choice, not an outstanding step.
        done: isConsentResolvedByUser(c, userId),
    }));
}

/** True while setup or consent still blocks the patient from the forms phase. */
export function arePriorDiaryPhasesOutstanding(todos: Pick<JourneyTodos, "setup" | "consent">): boolean {
    return todos.setup.length > 0 || todos.consent.some((item) => !item.done);
}

/**
 * Patient-relevant trackers on the diary (whether or not anything is due yet).
 * Used to preview the Forms phase during onboarding before setup/consent complete.
 */
export function buildFormsApplicableTodos(diary: StatefulDiaryT, authCtx: FormsAuthCtx): TodoItem[] {
    return (diary.trackers_view ?? [])
        .filter((t) => isFormRelevantForViewer((t as { roles?: string[] }).roles, authCtx))
        .map((t) => ({
            key: t.tracker_id,
            label: getTrackerDisplayLabel(t),
            done: false,
        }));
}

export function isFormsPhaseApplicable(diary: StatefulDiaryT | null | undefined, authCtx: FormsAuthCtx): boolean {
    if (!diary) return false;
    return buildFormsApplicableTodos(diary, authCtx).length > 0;
}

/**
 * Forms — one sub-step per patient-relevant tracker with actionable due work.
 * Omits the phase entirely when nothing is due (including while setup/consent block forms).
 */
export function buildFormsTodos(
    diary: StatefulDiaryT,
    authCtx: FormsAuthCtx,
    opts: { priorPhasesOutstanding?: boolean } = {}
): TodoItem[] {
    if (opts.priorPhasesOutstanding) return [];

    return (diary.trackers_view ?? [])
        .filter((t) => isFormRelevantForViewer((t as { roles?: string[] }).roles, authCtx))
        .filter((t) => getFormsToDoCounts([t as StatefulTrackerViewT], authCtx).taskCount > 0)
        .map((t) => ({
            key: t.tracker_id,
            label: getTrackerDisplayLabel(t),
            done: false,
        }));
}

export interface PhaseProgress {
    completedSteps: number;
    totalSteps: number;
}

function pushSetupPhase(
    phases: FlowPhase[],
    todos: JourneyTodos,
    opts: {
        applicable: boolean;
        includeCompleted: boolean;
        progress?: PhaseProgress;
        phaseLabels: JourneyPhaseLabels;
        skippedIncomplete?: boolean;
    }
) {
    if (!opts.applicable) return;
    const isOutstanding = todos.setup.length > 0 || Boolean(opts.skippedIncomplete);
    // On onboarding placeholders (includeCompleted=false) only outstanding setup is shown;
    // on diary pages a completed-but-applicable setup stays visible as "done".
    if (!isOutstanding && !opts.includeCompleted && !opts.progress) return;
    if (opts.progress) {
        phases.push({ label: opts.phaseLabels.setup, ...opts.progress });
        return;
    }
    phases.push({ label: opts.phaseLabels.setup, totalSteps: 1, completedSteps: isOutstanding ? 0 : 1 });
}

function pushConsentPhase(
    phases: FlowPhase[],
    todos: JourneyTodos,
    opts: {
        includeCompleted: boolean;
        progress?: PhaseProgress;
        phaseLabels: JourneyPhaseLabels;
    }
) {
    const consentDone = todos.consent.filter((i) => i.done).length;
    if (todos.consent.length === 0) return;
    if (!opts.includeCompleted && consentDone >= todos.consent.length && !opts.progress) return;
    if (opts.progress) {
        phases.push({ label: opts.phaseLabels.consent, ...opts.progress });
        return;
    }
    phases.push({
        label: opts.phaseLabels.consent,
        totalSteps: todos.consent.length,
        completedSteps: consentDone,
    });
}

function pushFormsPhase(
    phases: FlowPhase[],
    todos: JourneyTodos,
    opts: {
        includeCompleted: boolean;
        progress?: PhaseProgress;
        phaseLabels: JourneyPhaseLabels;
    }
) {
    if (opts.progress) {
        phases.push({ label: opts.phaseLabels.forms, ...opts.progress });
        return;
    }

    const formsDone = todos.forms.filter((i) => i.done).length;
    if (todos.forms.length === 0) return;
    if (!opts.includeCompleted && formsDone >= todos.forms.length) return;
    phases.push({
        label: opts.phaseLabels.forms,
        totalSteps: todos.forms.length,
        completedSteps: formsDone,
    });
}

/**
 * Due form work, or upcoming trackers while consent/setup still block forms.
 * Omits Forms on settled diaries when nothing is due (Guy: no empty Forms phase).
 */
function resolveFormsPhaseTodos(
    todos: JourneyTodos,
    formsApplicable: readonly TodoItem[] = [],
    opts: { previewUpcoming?: boolean } = {}
): TodoItem[] {
    if (todos.forms.length > 0) return [...todos.forms];
    if (formsApplicable.length === 0) return [];
    if (opts.previewUpcoming || arePriorDiaryPhasesOutstanding(todos)) return [...formsApplicable];
    return [];
}

/** Onboarding diary phases — same completion rules as after Profile, not empty previews. */
function buildOnboardingDiaryPhases(
    todos: JourneyTodos,
    hasDiary: boolean,
    setupApplicable: boolean,
    formsApplicable: readonly TodoItem[] = [],
    phaseLabels: JourneyPhaseLabels = DEFAULT_PHASE_LABELS,
    progress: {
        consentProgress?: PhaseProgress;
        setupProgress?: PhaseProgress;
        formsProgress?: PhaseProgress;
    } = {},
    setupSkippedIncomplete = false
): FlowPhase[] {
    if (!hasDiary) return [];
    const phases: FlowPhase[] = [];
    pushConsentPhase(phases, todos, {
        includeCompleted: true,
        progress: progress.consentProgress,
        phaseLabels,
    });
    pushSetupPhase(phases, todos, {
        applicable: setupApplicable,
        includeCompleted: true,
        progress: progress.setupProgress,
        phaseLabels,
        skippedIncomplete: setupSkippedIncomplete,
    });
    // Prefer explicit forms progress (entry completion). Otherwise mark applicable
    // trackers done when nothing is due and setup/consent are clear - same signal
    // as "Go to Diary" on /onboarding/complete.
    const formsTodos =
        todos.forms.length > 0
            ? [...todos.forms]
            : formsApplicable.length === 0
              ? []
              : arePriorDiaryPhasesOutstanding(todos)
                ? formsApplicable.map((item) => ({ ...item, done: false }))
                : formsApplicable.map((item) => ({ ...item, done: true }));
    pushFormsPhase(
        phases,
        { ...todos, forms: formsTodos },
        { includeCompleted: true, progress: progress.formsProgress, phaseLabels }
    );
    return phases;
}

/**
 * Build FlowProgress phases for the patient journey. Applicable phases stay visible
 * and completed phases remain filled so patients can see full progress.
 *
 * - Onboarding (Profile): wizard route sets fill while on `/onboarding/*`.
 * - Setup / Consent / Forms: always reflect diary DB state (same as complete CTAs).
 *
 * Setup applicability defaults to "has outstanding setup todos", but callers with the
 * diary in hand should pass {@link isSetupApplicableForDiary} so a completed Setup phase
 * stays visible next to later phases instead of vanishing once the last field is filled.
 */
export function buildJourneyPhases(
    todos: JourneyTodos,
    opts: {
        onboardingProgress?: PhaseProgress;
        consentProgress?: PhaseProgress;
        setupProgress?: PhaseProgress;
        formsProgress?: PhaseProgress;
        includeFuturePhases?: boolean;
        hasDiary?: boolean;
        setupApplicable?: boolean;
        /** Patient trackers on the diary — previews Forms on onboarding before anything is due. */
        formsApplicable?: readonly TodoItem[];
        /** Success screens: show Forms as the next stage even when nothing is due yet. */
        previewFormsPhase?: boolean;
        /** Localized phase labels (defaults to English). */
        phaseLabels?: Partial<JourneyPhaseLabels>;
        /** Date was skipped but is still unset: non-blocking, yet Setup remains incomplete. */
        setupSkippedIncomplete?: boolean;
    } = {}
): FlowPhase[] {
    const { includeFuturePhases = false, hasDiary = false } = opts;
    const setupApplicable = hasDiary && Boolean(opts.setupApplicable);
    const setupSkippedIncomplete = Boolean(opts.setupSkippedIncomplete);
    const phaseLabels: JourneyPhaseLabels = { ...DEFAULT_PHASE_LABELS, ...opts.phaseLabels };
    const phases: FlowPhase[] = [];

    if (opts.onboardingProgress) {
        phases.push({ label: phaseLabels.onboarding, ...opts.onboardingProgress });
        if (includeFuturePhases) {
            phases.push(
                ...buildOnboardingDiaryPhases(
                    todos,
                    hasDiary,
                    setupApplicable,
                    opts.formsApplicable ?? [],
                    phaseLabels,
                    {
                        consentProgress: opts.consentProgress,
                        setupProgress: opts.setupProgress,
                        formsProgress: opts.formsProgress,
                    },
                    setupSkippedIncomplete
                )
            );
            return phases;
        }
    } else if (hasDiary) {
        phases.push({ label: phaseLabels.onboarding, totalSteps: 1, completedSteps: 1 });
    }

    pushConsentPhase(phases, todos, {
        includeCompleted: true,
        progress: opts.consentProgress,
        phaseLabels,
    });
    pushSetupPhase(phases, todos, {
        applicable: setupApplicable,
        includeCompleted: true,
        progress: opts.setupProgress,
        phaseLabels,
        skippedIncomplete: setupSkippedIncomplete,
    });
    const formsPhaseTodos = resolveFormsPhaseTodos(todos, opts.formsApplicable ?? [], {
        previewUpcoming: opts.previewFormsPhase,
    });
    pushFormsPhase(
        phases,
        { ...todos, forms: formsPhaseTodos },
        { includeCompleted: true, progress: opts.formsProgress, phaseLabels }
    );

    return phases;
}

/** Profile / setup / consent work only — excludes due forms (those belong on the Diary badge). */
export function countPreFormJourneyTodos(todos: JourneyTodos): number {
    return (
        todos.onboarding.filter((item) => !item.done).length +
        todos.setup.length +
        todos.consent.filter((item) => !item.done).length
    );
}

/** Count outstanding work across all applicable journey phases (including forms). */
export function countOutstandingJourneyTodos(todos: JourneyTodos, opts?: { formEntryCount?: number }): number {
    const formsOutstanding = arePriorDiaryPhasesOutstanding(todos) ? 0 : (opts?.formEntryCount ?? todos.forms.length);

    return countPreFormJourneyTodos(todos) + formsOutstanding;
}

export interface PatientNotificationCounts {
    /** Profile nav badge — incomplete profile, diary setup, and consent (not due forms). */
    profileBadgeCount: number;
    /** Diaries nav badge — due form entries, with diary-list fallback when trackers are not hydrated. */
    diariesBadgeCount: number;
    /** Due entries on the active diary route / to-do page (0 while consent or setup blocks forms). */
    formEntryCount: number;
    /** Forms with due entries (matches FormsToDo itemCount). */
    formItemCount: number;
}

function countFormWorkAcrossDiaries(
    diaries: readonly DiaryForTodoCounters[],
    auth: FormsAuthCtx
): { formEntryCount: number; formItemCount: number } {
    return diaries.reduce(
        (acc, diary) => {
            if (diary.end_date !== null) {
                return acc;
            }
            const { itemCount, taskCount } = getFormsToDoCounts(trackersViewForTodo(diary), auth);
            return {
                formItemCount: acc.formItemCount + itemCount,
                formEntryCount: acc.formEntryCount + taskCount,
            };
        },
        { formItemCount: 0, formEntryCount: 0 }
    );
}

function countDiariesWithDueFallback(diaries: readonly DiaryForTodoCounters[], auth: FormsAuthCtx): number {
    return diaries.filter((d) => d.end_date === null && trackerDueForTodo(d, auth)).length;
}

/**
 * Single source for patient nav badges and form counters.
 * Nav bar, stepper (via todos), and FormsToDo (via getFormsToDoCounts) all derive from these rules.
 */
export function computePatientNotifications(
    todos: JourneyTodos,
    diaries: readonly DiaryForTodoCounters[],
    auth: FormsAuthCtx
): PatientNotificationCounts {
    const priorPhasesOutstanding = arePriorDiaryPhasesOutstanding(todos);
    const activeDiaries = diaries.filter((d) => d.end_date === null);
    const { formEntryCount: rawFormEntryCount, formItemCount: rawFormItemCount } = countFormWorkAcrossDiaries(
        activeDiaries,
        auth
    );
    const formEntryCount = priorPhasesOutstanding ? 0 : rawFormEntryCount;
    const formItemCount = priorPhasesOutstanding ? 0 : rawFormItemCount;
    const diariesFallback = priorPhasesOutstanding ? 0 : countDiariesWithDueFallback(activeDiaries, auth);

    return {
        profileBadgeCount: countPreFormJourneyTodos(todos),
        diariesBadgeCount: formEntryCount > 0 ? formEntryCount : diariesFallback,
        formEntryCount,
        formItemCount,
    };
}

export function countOutstandingJourneyTodosByPhase(todos: JourneyTodos): Record<JourneyPhaseKey, number> {
    return {
        onboarding: todos.onboarding.filter((item) => !item.done).length,
        setup: todos.setup.length,
        consent: todos.consent.filter((item) => !item.done).length,
        forms: todos.forms.length,
    };
}

export { getUnsignedConsents };
